import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch user profile on mount/token change
  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/user/me`);
      setUser(response.data);
      
      // Also fetch wallet
      const walletResponse = await axios.get(`${API}/user/wallet`);
      setWallet(walletResponse.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Token might be invalid
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Fetch wallet after login
    try {
      const walletResponse = await axios.get(`${API}/user/wallet`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setWallet(walletResponse.data);
    } catch (e) {
      console.error('Failed to fetch wallet:', e);
    }
    
    return userData;
  };

  const register = async (email, password, name, referralCode = null, recaptchaToken = null) => {
    const payload = { email, password, name };
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    if (recaptchaToken) {
      payload.recaptcha_token = recaptchaToken;
    }
    const response = await axios.post(`${API}/auth/register`, payload);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Fetch wallet after registration
    try {
      const walletResponse = await axios.get(`${API}/user/wallet`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setWallet(walletResponse.data);
    } catch (e) {
      console.error('Failed to fetch wallet:', e);
    }
    
    return userData;
  };

  const loginWithGoogle = async (code) => {
    const response = await axios.post(`${API}/auth/google/callback`, { code });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const loginWithGoogleSession = async (accessToken, userData) => {
    // Store token from Emergent Google OAuth session exchange
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
    
    // Fetch wallet after Google login
    try {
      const walletResponse = await axios.get(`${API}/user/wallet`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setWallet(walletResponse.data);
    } catch (e) {
      console.error('Failed to fetch wallet:', e);
    }
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setWallet(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const refreshWallet = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/user/wallet`);
      setWallet(response.data);
    } catch (e) {
      console.error('Failed to refresh wallet:', e);
    }
  };

  const isAdmin = user?.role === 'admin';

  const value = {
    user,
    token,
    wallet,
    loading,
    isAuthenticated: !!user,
    isAdmin,
    login,
    register,
    loginWithGoogle,
    loginWithGoogleSession,
    logout,
    refreshWallet,
    refreshUser: fetchUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
